import { TournamentRecurringType, TournamentStatus, TournamentType } from '@repo/database/src/types';
import type {
  TournamentBlindLevel,
  TournamentBlindLevelSnapshot,
  TournamentPayout,
  TournamentRegistration,
  TournamentTicket,
} from '@repo/database/src/types';
import type {
  TournamentDefaultArgs,
  TournamentGetPayload,
  TournamentTemplateCreateInput,
  TournamentTemplateUpdateInput,
} from '@repo/database/src/types';

import { PlayerStats } from '../../event-types';
import { TournamentTemplateWComputed } from '../../tournament-types';
import { TournamentInfoDto } from './tournament-info.dto';
import { TournamentTableState } from './tournament-types';

// ---------------------------------------------------
// Tournament Template DTOs
// ---------------------------------------------------
export interface TournamentTemplateDto extends TournamentTemplateWComputed {
  lateRegistrationUpToLevel?: number;
}

export const DEFAULT_BREAK_MINUTE = 55;
export const DEFAULT_BREAK_DURATION_IN_MINUTES = 5;

// ---------------------------------------------------
// Tournament DTOs
// ---------------------------------------------------

// Standard tournament includes - used by most queries
// NOTE: Does NOT include registrations for performance reasons
// Use TournamentRegistrationService or TournamentRepository.getTournamentRegistrations() for registration data
export const tournamentWithIncludes = {
  include: {
    blindStructure: {
      include: {
        levels: true,
      },
    },
    payoutStructure: true,
    parentTournament: true,
    // NO registrations, tables, or rebuyAddonLogs for performance
  },
} satisfies TournamentDefaultArgs;

export const tournamentWithPayoutBrackets = {
  include: {
    blindStructure: {
      include: {
        levels: true,
      },
    },
    payoutStructure: {
      include: {
        brackets: true,
      },
    },
    parentTournament: true,
    // NO registrations, tables, or rebuyAddonLogs for performance
  },
} satisfies TournamentDefaultArgs;

export type TournamentDto = TournamentGetPayload<typeof tournamentWithIncludes> & {
  playersRemaining?: number;
  totalBuyin: number;
};

export type TournamentPayoutWithTicket = TournamentPayout & {
  ticket?: TournamentTicket | null;
};

export type TournamentWithUserContext = TournamentDto & {
  userRegistration?: TournamentRegistration | null;
  userPayout?: TournamentPayoutWithTicket | null;
};

export type TournamentWithPayoutBracketsDto = TournamentGetPayload<typeof tournamentWithPayoutBrackets> & {
  playersRemaining?: number;
  totalBuyin: number;
};

// Tournament creation overrides interface
export interface TournamentCreationOverrides extends Partial<TournamentDto> {
  recurringType?: TournamentRecurringType | null;
  recurringFrequency?: number;
}

export const tournamentListingLightIncludes = {} satisfies TournamentDefaultArgs;

export type TournamentListingLight = TournamentGetPayload<typeof tournamentListingLightIncludes> & {
  playersRemaining?: number;
  parentTournament?: TournamentListingLight | null;
  totalBuyin: number;
};

// Lightweight DTO for tournament listings - only includes fields actually used in UI
export interface TournamentListingDto {
  // Core identification
  id: string;
  name: string;
  game: string;
  tournamentType: TournamentType;
  category: string;
  status: TournamentStatus;

  contractAddress: string;

  // Display fields
  entries: number;
  playersRemaining: number;
  buyIn: number;
  fee: number;
  totalBuyin: number;
  prizePool: number;
  guaranteedPrize: number;
  overlay: number;
  maxReentries: number;

  // Timing fields (stored as ISO strings in Redux to avoid serialization issues)
  startTime: Date | string | null;
  lateRegistrationTime: Date | string | null;
  registrationOpenTime: Date | string | null;

  // User-specific fields (optional - only for user's tournaments)
  myPosition?: number | null;
  myRegularPrize?: number | null;
  myPrizeTicket?: TournamentTicket | null;
  myTournamentDollarsAwarded?: number;

  // Fields needed for calculations
  minPlayers: number;
  maxPlayers: number;

  // Real-time update fields
  currentLevel?: number;
  levelEndAt?: Date | null;
  blindInterval?: number | null;

  // Customization fields
  icon?: string | null;
  titleColor?: string | null;
  lobbyIcons?: string[];

  // Optional fields for future use
  minimumTierRequired?: string | null;
  finishedAt?: Date | null;
  cancellationReason?: string | null;

  tableId?: string | null;

  startingChips?: number | null;

  // Satellite tournament info
  parentTournament?: TournamentListingDto | null;

  ticketCount?: number;
  tournamentDollarsPrizePool?: number;
}

// Response DTO that can use either full or light tournaments
export interface LobbyTournamentsLightResponseDto {
  data: TournamentListingDto[];
  total: number;
  totalFiltered: number;
}

// ---------------------------------------------------
// (Optional) Refactored Create / Update interfaces
// ---------------------------------------------------

// Instead of manually writing interfaces, you can leverage Prisma's generated types
// for stricter type-safety and consistency across your application:

export type CreateTournamentTemplateData = Omit<TournamentTemplateCreateInput, 'payoutStructure' | 'blindStructure'> & {
  payoutStructureId: string;
  blindStructureId: string;
  lateRegistrationUpToLevel?: number;

  startTime?: Date; // Optional, not defined in Sit & Go
  registrationOpenTime?: Date; // Optional, will default to startTime - 1 hour
};

export type UpdateTournamentTemplateData = TournamentTemplateUpdateInput & {
  payoutStructureId?: string;
  blindStructureId?: string;
  lateRegistrationUpToLevel?: number;
  totalBuyin?: number;
};

export type CreateTournamentData = CreateTournamentTemplateData & {
  startTime?: Date; // Optional, not defined in Sit & Go
  registrationOpenTime?: Date; // Optional, will default to startTime - 1 hour
};

/**
 * Tournament section for client-side organization
 * Note: This is created on the client side, not returned from the backend
 */
export interface TournamentSectionDto {
  type: 'section';
  name: string;
  tournaments: TournamentInfoDto[];
}

export interface TournamentTableStateDto extends TournamentTableState {
  playerStats?: PlayerStats | null;
  breakStartMinute?: number;
  levelsGoingUpStartTime?: number;
}

/**
 * Represents a single game within the tournament's game order (for mixed games).
 * Includes the specific variant and the relevant blind level details for that game.
 */
export interface TournamentGameOrderDto {
  game: string;
  level: Partial<TournamentBlindLevelSnapshot>;
}

export interface TournamentRegistrationDto {
  id: string;
  userId: string;
  tournamentId: string;
  registeredAt: Date;
  ticketId: string | null;
  rebuysCount: number;
  addonsCount: number;
  isEliminated: boolean;
  eliminationAt: Date | null;
  finalPosition: number | null;
  prizeAwarded: number;
}

export interface TournamentDetailsDto {
  payouts: TournamentDetailsPayoutsDto;
  tables: TournamentDetailsTablesDto;
  players: TournamentDetailsPlayersDto;
  stackInfo: TournamentDetailsStackInfoDto;
  /** The order and details of games for the current level, especially for mixed games. */
  gameOrder: TournamentGameOrderDto[];
}

export interface PayoutDto {
  rank: number;
  prize: number;
  user?: { alias: string; countryCode?: string; entries?: number };
}

export interface PrizeDto {
  rankFrom: number;
  rankTo: number;
  amount: number;
}

export interface TournamentDetailsPayoutsDto {
  placesPaid: number;
  totalPrizes: number;
  nextPrize: PrizeDto | null;
  payouts: PayoutDto[];
  total: number;
}

export interface TournamentPayoutTransactionDto {
  id: string;
  createdAt: Date;
  amount: number;
  status: string;
  ticketType: string | null;
  txId: string | null;
  ticketOriginTxId: string | null;
  user: {
    alias: string;
  };
}

export interface TournamentResultDto {
  rank: number | null;
  alias: string;
  countryCode: string | null;
  prizeAwarded: number | null;
  entries?: number;
  ticketAwarded?: boolean;
  tournamentDollarsAwarded?: number;
}

export interface TournamentResultsDto {
  results: TournamentResultDto[];
  total: number;
}

export interface TournamentTableDto {
  tableId: string;
  tableNumber: number;
  players: number;
  mostChips: number;
  leastChips: number;
}
export interface TournamentDetailsTablesDto {
  tables: TournamentTableDto[];
  total: number;
}

export interface TournamentPlayerDto {
  userId: string;
  rank: number;
  alias: string;
  countryCode: string;
  chips: number;
  bounty?: number;
  isEliminated?: boolean;
  entries: number;
  prize?: number;
  ticketType?: string | null;
}

export interface TournamentDetailsPlayersDto {
  players: TournamentPlayerDto[];
  total: number;
}

export interface TournamentDetailsStackInfoDto {
  smallestStack: number;
  averageStack: number;
  largestStack: number;
  smallestStackBB: number;
  averageStackBB: number;
  largestStackBB: number;
}

export interface TournamentOverviewStatsDto {
  dailyRake: {
    date: string;
    totalAmount: number;
  }[];
  dailyPlayers: {
    date: string;
    playersCount: number;
  }[];
  participantsByTimeOfDay: {
    hour: number;
    participants: number;
  }[];
  rakeByGameType: {
    game: string;
    totalAmount: number;
  }[];
  rakeByBuyIn: {
    buyIn: number;
    totalAmount: number;
  }[];
  participantsByBuyIn: {
    buyIn: number;
    participants: number;
  }[];
}

export interface TournamentBlindStructureDto {
  id?: string;
  name?: string;
  blindStructure: TournamentBlindLevel[];
  total: number;
}
